import { useEffect, useRef } from "react";
import Select, { SelectProps, transformDataToSelectOption } from "@/components/atoms/Select";
import { useGetCountries } from "@/services/RegisterCompany";
import { useTranslation } from "react-i18next";
import { Country } from "@/types";
import { useAuth } from "@/contexts/AuthContext";

type keyOptions = Extract<keyof Country, string>;

interface SelectPropsExtended extends SelectProps {
  valueKey: keyOptions;
  labelKey: keyOptions;
  setSelectedCountry?: (country: Country) => void;
  defaultCountry?: string | null | undefined;
}

function SelectCountry({
  valueKey,
  labelKey,
  setSelectedCountry,
  onChange,
  value,
  defaultCountry,
  ref,
  ...restProps
}: SelectPropsExtended) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: countryOptions, isLoading } = useGetCountries(user?.language?.id ?? 1, {
    enabled: !!user?.language?.id,
  });
  const sourceData = countryOptions || [];
  const options = transformDataToSelectOption(sourceData, valueKey || "id", labelKey || "name", t);

  const hasSetDefaultRef = useRef(false);

  useEffect(() => {
    if (!setSelectedCountry || hasSetDefaultRef.current || !sourceData.length) return;

    const val = value ?? defaultCountry;
    if (val === undefined || val === null || val === "") return;

    const selectedValue = valueKey === "id" ? Number(val) : val;
    const foundCountry = sourceData.find((c) => (c as any)[valueKey] === selectedValue);

    if (foundCountry) {
      setSelectedCountry(foundCountry);
      hasSetDefaultRef.current = true;
    }
  }, [sourceData, value, defaultCountry, valueKey, setSelectedCountry]);

  const handleOnChange = (val: string | null, option: any) => {
    onChange?.(val, option);

    if (setSelectedCountry && val != null && sourceData.length > 0) {
      const selectedValue = valueKey === "id" ? Number(val) : val;
      const foundCountry = sourceData.find((c) => (c as any)[valueKey] === selectedValue);
      if (foundCountry) setSelectedCountry(foundCountry);
    }
  };

  return (
    <Select
      ref={ref}
      id="country-select"
      data={options}
      loading={isLoading}
      comboboxProps={{ withinPortal: true, zIndex: 10001 }}
      onChange={handleOnChange}
      value={value ?? undefined}
      defaultValue={defaultCountry ?? undefined}
      {...restProps}
    />
  );
}

export default SelectCountry;
